import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, isStaff, STAFF_ROLES } from "./context/AuthContext";
import PublicLayout from "./components/PublicLayout";
import StaffLayout from "./components/StaffLayout";
import Landing from "./pages/public/Landing";
import Shop from "./pages/public/Shop";
import Book from "./pages/public/Book";
import About from "./pages/public/About";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import AuthCallback from "./pages/auth/AuthCallback";
import Portal from "./pages/portal/Portal";
import Dashboard from "./pages/staff/Dashboard";
import POS from "./pages/staff/POS";
import Inventory from "./pages/staff/Inventory";
import Customers from "./pages/staff/Customers";
import Grooming from "./pages/staff/Grooming";
import Boarding from "./pages/staff/Boarding";
import Reports from "./pages/staff/Reports";
import Settings from "./pages/staff/Settings";

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F2FBFB]">
      <div className="animate-pulse font-display font-bold text-[#0E7C84] text-xl">Pet's Pantry</div>
    </div>
  );
}

function RequireAuth({ children, roles }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <FullPageLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={isStaff(user) ? "/staff" : "/portal"} replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Detect OAuth session_id synchronously during render (prevents race with route guards)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/book" element={<Book />} />
        <Route path="/about" element={<About />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/portal" element={<RequireAuth roles={["customer"]}><Portal /></RequireAuth>} />
      </Route>
      <Route path="/staff" element={<RequireAuth roles={STAFF_ROLES}><StaffLayout /></RequireAuth>}>
        <Route index element={<Dashboard />} />
        <Route path="pos" element={<RequireAuth roles={["admin", "manager", "cashier"]}><POS /></RequireAuth>} />
        <Route path="inventory" element={<RequireAuth roles={["admin", "manager"]}><Inventory /></RequireAuth>} />
        <Route path="customers" element={<Customers />} />
        <Route path="grooming" element={<RequireAuth roles={["admin", "manager", "groomer", "cashier"]}><Grooming /></RequireAuth>} />
        <Route path="boarding" element={<RequireAuth roles={["admin", "manager", "kennel_staff", "cashier"]}><Boarding /></RequireAuth>} />
        <Route path="reports" element={<RequireAuth roles={["admin", "manager"]}><Reports /></RequireAuth>} />
        <Route path="settings" element={<RequireAuth roles={["admin", "manager"]}><Settings /></RequireAuth>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
